import {PipettingParameterFieldKey, TaskType} from 'constants/dataEnum';
import PipettingProfileExpanderButton from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileExpanderButton';
import PipettingProfileItems from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileItem/PipettingProfileItems';
import PipettingProfileWrapper from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileWrapper';
import {isTaskTipCountValid} from 'lib/cardAndTaskHelper';
import PropTypes from 'prop-types';
import {useState} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import {
  setTaskEnableMixing,
  setTaskMixingCycle,
  setTaskMixingPositionAspirate,
  setTaskMixingPositionDispense,
  setTaskMixingSpeed,
  setTaskMixingVolume,
  setTaskPrecisionDispense,
  setTaskRetractSpeed,
  setTaskSpeed,
  setTaskTipPosition,
  setTaskTipTouchOff,
  setTaskTipTouchOffDelay,
  setTaskTipTouchOffDirectionX,
  setTaskTipTouchOffDirectionY,
  setTaskTipTouchOffHeight,
} from 'stores/actions/cardStateAction';
import {showFailedToUpdatePipettingValueModal} from 'stores/business/commonTaskListBusiness';
import {updatePipettingTipPositionOffset} from 'stores/business/pipettingParameterBusiness';
import {updateRunningProtocol} from 'stores/business/protocolManagementBusiness';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';
import {getSelectedCardSelector, getTasksInCardSelector} from 'stores/selectors/cardStateSelector';
import {getLabwareDataById} from 'stores/selectors/labwareStateSelectors';

function PoolingPipettingProfile({runtimeMode, task}) {
  const dispatch = useDispatch();
  const [isPipettingProfileExpanded, setIsPipettingProfileExpanded] = useState();
  const {type: taskType, labwareID} = task || {};
  const selectedCard = useSelector(getSelectedCardSelector);
  const selectedTasks = useSelector(getTasksInCardSelector(selectedCard[0]));
  const labware = useSelector(getLabwareDataById(labwareID));
  const isAspiratePipettingProfile = taskType === TaskType.ASPIRATE;
  const isDispensePipettingProfile = taskType === TaskType.DISPENSE;

  const postUpdateAction = () => {
    if (runtimeMode) {
      dispatch(updateRunningProtocol());
    } else {
      dispatch(globalAddUndoItem());
    }
  };

  const updatePipettingValue = (updateFunction, value) => {
    batch(() => {
      Object.values(selectedTasks)
        .filter((item) => item.type === taskType)
        .forEach((tsk) => dispatch(updateFunction(tsk.ID, value)));
      postUpdateAction();
    });
  };

  const updatePipettingTipPositionValue = (direction, value) => {
    batch(() => {
      Object.values(selectedTasks)
        .filter((item) => item.type === taskType)
        .forEach((tsk, idx) => dispatch(updatePipettingTipPositionOffset(direction, value, tsk, idx !== 0)));
      postUpdateAction();
    });
  };

  // #region change method
  const setTipPosition = (value) => updatePipettingValue(setTaskTipPosition, value);
  const setTipXPosition = (value) => updatePipettingTipPositionValue('x', value);
  const setTipYPosition = (value) => updatePipettingTipPositionValue('y', value);
  const setPrecisionDispense = (value) => updatePipettingValue(setTaskPrecisionDispense, value);
  const setTipTouchOffDelay = (value) => updatePipettingValue(setTaskTipTouchOffDelay, value);
  const setEnableTipTouchOff = (value) => {
    if (!isTaskTipCountValid(task, labware)) {
      dispatch(showFailedToUpdatePipettingValueModal(PipettingParameterFieldKey.TIP_TOUCH_OFF));
      return;
    }

    updatePipettingValue(setTaskTipTouchOff, value);
  };

  const setSpeed = (value) => updatePipettingValue(setTaskSpeed, value);
  const setRetractSpeed = (value) => updatePipettingValue(setTaskRetractSpeed, value);
  const setEnableMixing = (value) => updatePipettingValue(setTaskEnableMixing, value);
  const setMixingVolume = (value) => updatePipettingValue(setTaskMixingVolume, value);
  const setMixingCycle = (value) => updatePipettingValue(setTaskMixingCycle, value);
  const setMixingSpeed = (value) => updatePipettingValue(setTaskMixingSpeed, value);
  const setMixingPositionAspirate = (value) => updatePipettingValue(setTaskMixingPositionAspirate, value);
  const setMixingPositionDispense = (value) => updatePipettingValue(setTaskMixingPositionDispense, value);
  const setTipTouchOffHeight = (value) => updatePipettingValue(setTaskTipTouchOffHeight, value);
  const setTipTouchOffDirectionX = (value) => updatePipettingValue(setTaskTipTouchOffDirectionX, value);
  const setTipTouchOffDirectionY = (value) => updatePipettingValue(setTaskTipTouchOffDirectionY, value);
  // #endregion

  return (
    <div>
      {isPipettingProfileExpanded && (
        <PipettingProfileWrapper>
          <PipettingProfileItems.TipPosition
            task={task}
            onChange={setTipPosition}
            onXTipPositionOnChange={setTipXPosition}
            onYTipPositionOnChange={setTipYPosition}
          />
          {isAspiratePipettingProfile && (
            <>
              <PipettingProfileItems.Speed task={task} onChange={setSpeed} />
              <PipettingProfileItems.RetractSpeed task={task} onChange={setRetractSpeed} />
            </>
          )}
          {isDispensePipettingProfile && (
            <>
              <PipettingProfileItems.PrecisionDispense task={task} onChange={setPrecisionDispense} />
              <PipettingProfileItems.Speed task={task} onChange={setSpeed} />
              <PipettingProfileItems.RetractSpeed task={task} onChange={setRetractSpeed} />
            </>
          )}
          <PipettingProfileItems.EnableMixing task={task} onChange={setEnableMixing} />
          <PipettingProfileItems.MixingDetailGroup
            task={task}
            onChangeVolume={setMixingVolume}
            onChangeCycle={setMixingCycle}
            onChangeMixingPositionAspirate={setMixingPositionAspirate}
            onChangeMixingPositionDispense={setMixingPositionDispense}
            onChangeMixingSpeed={setMixingSpeed}
          />
          <PipettingProfileItems.TipTouchOff
            task={task}
            onChangeSwitch={setEnableTipTouchOff}
            onChangeDelay={setTipTouchOffDelay}
            onChangeHeight={setTipTouchOffHeight}
            onChangeDirectionX={setTipTouchOffDirectionX}
            onChangeDirectionY={setTipTouchOffDirectionY}
          />
        </PipettingProfileWrapper>
      )}
      <PipettingProfileExpanderButton isExpanded={isPipettingProfileExpanded} onClick={setIsPipettingProfileExpanded} />
    </div>
  );
}

PoolingPipettingProfile.propTypes = {
  runtimeMode: PropTypes.bool,
  task: PropTypes.object.isRequired,
};

PoolingPipettingProfile.defaultProps = {
  runtimeMode: false,
};

export default PoolingPipettingProfile;
